import React, { useCallback, useEffect, useRef, useState } from "react";
import { Wand2, Square, CheckCircle2, AlertTriangle, Ban, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { staffLabelText, useAvatars, resolveStaff } from "./StaffAvatar";

/**
 * Phase 91 — Auto-assign by territory runs as a background job (batches of 500).
 * This hook starts / resumes / polls / cancels the job; the card renders live progress.
 * The user can leave the page: on return we look up the latest run and resume polling.
 */
const POLL_MS = 2000;
const storageKey = (code) => `auto_assign_job_${code}`;

export function useAutoAssignJob(code, { onFinished } = {}) {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const timer = useRef(null);
  const finishedRef = useRef(null);
  finishedRef.current = onFinished;
  const lastStatus = useRef(null);

  const stopPolling = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

  const applyJob = useCallback((j) => {
    setJob(j);
    if (!j) return;
    if (j.status === "processing") {
      localStorage.setItem(storageKey(code), j.id);
    } else {
      localStorage.removeItem(storageKey(code));
      stopPolling();
      if (lastStatus.current === "processing") finishedRef.current?.(j); // transitioned while we watched
    }
    lastStatus.current = j.status;
  }, [code]);

  const poll = useCallback(async (jobId) => {
    try {
      const r = await api.get(`/t/${code}/assignments/auto-assign/jobs/${jobId}`);
      applyJob(r.data);
    } catch (e) {
      if (e?.response?.status === 404) { localStorage.removeItem(storageKey(code)); stopPolling(); setJob(null); }
    }
  }, [code, applyJob]);

  const watch = useCallback((jobId) => {
    stopPolling();
    poll(jobId);
    timer.current = setInterval(() => poll(jobId), POLL_MS);
  }, [poll]);

  // resume on mount: a job we were watching, else the latest run for the "last run" summary
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/t/${code}/assignments/auto-assign/jobs`, { params: { limit: 1 } });
        const latest = r.data?.[0];
        if (!alive) return;
        if (latest?.status === "processing") { lastStatus.current = "processing"; watch(latest.id); }
        else if (latest) { lastStatus.current = latest.status; setJob(latest); localStorage.removeItem(storageKey(code)); }
        else {
          const stored = localStorage.getItem(storageKey(code));
          if (stored) watch(stored);
        }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; stopPolling(); };
  }, [code, watch]);

  const start = useCallback(async () => {
    setStarting(true);
    try {
      const r = await api.post(`/t/${code}/assignments/auto-assign`);
      if (r.data.already_running) toast.info("An auto-assign run is already in progress — showing its progress");
      else toast.success("Auto-assign started in the background — you can leave this page, it will keep running");
      lastStatus.current = "processing";
      watch(r.data.job_id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setStarting(false);
    }
  }, [code, watch]);

  const cancel = useCallback(async () => {
    if (!job?.id) return;
    setCancelling(true);
    try {
      await api.post(`/t/${code}/assignments/auto-assign/jobs/${job.id}/cancel`);
      toast.info("Stopping after the current batch — assignments already made are kept");
      poll(job.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCancelling(false);
    }
  }, [code, job, poll]);

  return { job, start, cancel, starting, cancelling, refresh: () => job && poll(job.id), running: job?.status === "processing" };
}

const STATUS_META = {
  processing: { label: "Running", cls: "bg-sky-100 text-sky-800 border-sky-200", icon: RefreshCw },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  cancelled: { label: "Stopped", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Ban },
  failed: { label: "Interrupted", cls: "bg-red-100 text-red-800 border-red-200", icon: AlertTriangle },
};

export function AutoAssignProgressCard({ code, job, onCancel, cancelling, onDismiss }) {
  const dir = useAvatars(code);
  const lbl = (id, name) => staffLabelText(name, resolveStaff(dir, id, name)?.employee_id, { unknown: !resolveStaff(dir, id, name) });
  if (!job) return null;
  const meta = STATUS_META[job.status] || STATUS_META.processing;
  const Icon = meta.icon;
  const total = job.total || 0;
  const pct = total ? Math.min(100, Math.round((job.processed / total) * 100)) : (job.status === "processing" ? 0 : 100);
  const running = job.status === "processing";
  return (
    <Card className={`shadow-sm mb-4 ${running ? "border-sky-200" : ""}`} data-testid="auto-assign-progress-card" data-status={job.status}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Wand2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <p className="text-sm font-medium">Auto-assign by territory</p>
            <Badge variant="outline" className={`text-[11px] gap-1 ${meta.cls}`} data-testid="auto-assign-status-badge">
              <Icon className={`h-3 w-3 ${running ? "animate-spin" : ""}`} /> {meta.label}
            </Badge>
            {job.stage === "finalizing" && <span className="text-xs text-muted-foreground">notifying collectors…</span>}
          </div>
          <div className="flex items-center gap-2">
            {running ? (
              <Button size="sm" variant="outline" onClick={onCancel} disabled={cancelling || job.cancel_requested} data-testid="auto-assign-cancel-button">
                <Square className="h-3.5 w-3.5 mr-1.5" /> {job.cancel_requested ? "Stopping…" : "Stop"}
              </Button>
            ) : onDismiss ? (
              <Button size="sm" variant="ghost" onClick={onDismiss} data-testid="auto-assign-dismiss-button">Dismiss</Button>
            ) : null}
          </div>
        </div>
        <div>
          <Progress value={pct} className="h-2" data-testid="auto-assign-progress-bar" />
          <p className="text-xs text-muted-foreground mt-1.5 tabular-nums" data-testid="auto-assign-progress-text">
            {total ? `${job.processed.toLocaleString("en-IN")} of ${total.toLocaleString("en-IN")} properties scanned (${pct}%)` : "Preparing…"}
            {job.batches_done ? ` • ${job.batches_done} batch${job.batches_done === 1 ? "" : "es"} of ${job.batch_size}` : ""}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
          <Stat label="Newly assigned" value={job.assigned} tone="text-emerald-700" testId="auto-assign-stat-assigned" />
          <Stat label="Moved by territory" value={job.moved ?? 0} tone={job.moved ? "text-sky-700" : ""} testId="auto-assign-stat-moved"
            hint="Taken from collectors who no longer cover the property (unmapped, inactive or restricted to other mohallas)" />
          <Stat label="Already right" value={job.already_assigned} testId="auto-assign-stat-already" hint="Holder's current territory still covers the property" />
          <Stat label="No covering collector" value={(job.no_collector ?? 0) + (job.uncovered ?? 0)} tone={(job.no_collector || job.uncovered) ? "text-amber-700" : ""} testId="auto-assign-stat-nocollector" />
          <Stat label="Collectors" value={job.collectors?.length ?? Object.keys(job.per_collector || {}).length} testId="auto-assign-stat-collectors" />
        </div>
        {!running && job.collectors?.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="auto-assign-collector-breakdown">
            {job.collectors.slice(0, 6).map((c) => `${lbl(c.id, c.name)}: +${c.assigned}`).join(" • ")}{job.collectors.length > 6 ? ` • +${job.collectors.length - 6} more` : ""}
          </p>
        )}
        {!running && job.released?.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="auto-assign-released-breakdown">
            Released: {job.released.slice(0, 6).map((c) => `${lbl(c.id, c.name)}: −${c.moved}`).join(" • ")}{job.released.length > 6 ? ` • +${job.released.length - 6} more` : ""}
          </p>
        )}
        {job.error && <p className="text-xs text-red-700" data-testid="auto-assign-error">{job.error}</p>}
        <p className="text-[11px] text-muted-foreground">
          {running ? "Runs in the background — you can leave this page and come back." : `Started ${fmtDateTime(job.started_at)} by ${job.started_by?.name ? staffLabelText(job.started_by.name, job.started_by.employee_id) : "—"}${job.finished_at ? ` • finished ${fmtDateTime(job.finished_at)}` : ""}`}
        </p>
      </CardContent>
    </Card>
  );
}

const Stat = ({ label, value, tone = "", testId, hint }) => (
  <div className="rounded-md border bg-muted/30 px-3 py-2" data-testid={testId} title={hint}>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className={`font-semibold tabular-nums ${tone}`}>{(value ?? 0).toLocaleString("en-IN")}</p>
  </div>
);
